import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "./toast-provider";
import type { SSEEvent } from "@/hooks/use-sse";
import {
  getNotificationRequestId,
  isFullySignedEvent,
  isPositiveNotificationEvent,
} from "@/lib/realtime-consumers";

const EVENT_MESSAGES: Record<string, (data: Record<string, unknown>) => { title: string; description: string }> = {
  "request.verified_access": (d) => ({
    title: "Verified Access Reached",
    description: `${d.recipientName ?? "Someone"} completed recipient verification for "${d.documentTitle ?? "your document"}"`,
  }),
  "request.viewed": (d) => ({
    title: "Document Viewed",
    description: `${d.recipientName ?? "Someone"} viewed "${d.documentTitle ?? "your document"}"`,
  }),
  "request.signed": (d) => ({
    title: d.allComplete ? "All Signatures Complete!" : "Document Signed!",
    description: d.allComplete
      ? `"${d.documentTitle ?? "Your document"}" has been fully signed`
      : `${d.recipientName ?? "Someone"} signed "${d.documentTitle ?? "your document"}"`,
  }),
  "request.declined": (d) => ({
    title: "Document Declined",
    description: `${d.recipientName ?? "Someone"} declined "${d.documentTitle ?? "your document"}"`,
  }),
  "request.expired": (d) => ({
    title: "Request Expired",
    description: `"${d.documentTitle ?? "Your document"}" has expired`,
  }),
  // One-time activation celebration. Fires the first time a user
  // successfully sends a signing request.
  "user.first_send_completed": (d) => ({
    title: "You just sent your first document",
    description: `"${d.documentTitle ?? "Your document"}" is out the door. Watch this space as your recipient opens and signs.`,
  }),
};

interface EventListenerProps {
  lastEvent: SSEEvent | null;
}

export function EventListener({ lastEvent }: EventListenerProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const lastIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!lastEvent || lastEvent.id === lastIdRef.current) return;
    lastIdRef.current = lastEvent.id;

    const messageBuilder = EVENT_MESSAGES[lastEvent.type];
    if (!messageBuilder) return;

    const msg = messageBuilder(lastEvent.data);
    const isFullyComplete = isFullySignedEvent(lastEvent);
    const isPositive = isPositiveNotificationEvent(lastEvent);
    const requestId = getNotificationRequestId(lastEvent);

    if (isFullyComplete && requestId) {
      toast({
        type: "success",
        title: msg.title,
        description: msg.description,
        action: {
          label: "View & Download",
          onClick: () => navigate(`/requests/${requestId}`),
        },
      });
    } else {
      toast({
        type: isPositive ? "success" : "info",
        title: msg.title,
        description: msg.description,
      });
    }
  }, [lastEvent, toast, navigate]);

  return null;
}
